<template>
  <div
    v-if="!mobileBreakpoints()"
    class="mt-4"
  >
    <div v-if="isAnimation">
      <strong>
        {{ $t('ServiceCard.timeIntervallLabel') }}
      </strong>
      <v-row class="ma-0 mt-2">
        <v-col
          :cols="dailyData ? 12 : 6"
          class="py-0 pl-0"
        >
          <v-menu
            ref="forecastRangeMenu"
            v-model="forecastRangeMenu"
            :close-on-content-click="false"
            :return-value.sync="forecastStartDate"
            :attach="`.forecastRangeMenu${serviceCardId}`"
            transition="scale-transition"
            offset-y
            max-width="300px"
            min-width="300px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="forecastStartDate"
                chips
                small-chips
                :label="$t('ServiceCard.date')"
                :class="`forecastRangeMenu${serviceCardId}`"
                :disabled="map.disableAnimationPlay"
                prepend-icon="mdi-calendar"
                readonly
                clearable
                hide-details
                v-bind="attrs"
                v-on="on"
                @click:clear="forecastStartTime = ''"
              />
            </template>
            <v-date-picker
              v-model="forecastStartDate"
              no-title
              scrollable
              full-width
              :min="minDate"
              :max="maxDate"
              :locale="app_user.app_user_data[0].language"
              first-day-of-week="1"
              @input="$refs.forecastRangeMenu.save(forecastStartDate);"
            />
          </v-menu>
        </v-col>
        <v-col
          v-if="!dailyData"
          cols="6"
          class="py-0 pr-0"
        >
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="forecastStartTime"
                :label="$t('ServiceCard.hourly')"
                :disabled="map.disableAnimationPlay"
                prepend-icon="mdi-clock-time-four-outline"
                clear-icon="mdi-calendar"
                value="00:00:00"
                type="time"
                max-width="290px"
                min-width="290px"
                hide-details
                v-bind="attrs"
                v-on="on"
              />
            </template>
            <span>{{ $t('ServiceCard.localTime') }}</span>
          </v-tooltip>
        </v-col>
      </v-row>
      <div class="d-flex justify-center mt-4">
        <v-btn
          id="submit"
          color="#0da344"
          small
          style="color:white"
          :disabled="disableConfirmButton()"
          @click="changeDate()"
        >
          {{ $t('ServiceCard.confirmFilter') }}
        </v-btn>
      </div>
    </div>
    <v-alert
      :value="alert"
      dense
      type="warning"
      icon="mdi-alert-outline"
      class="mt-4"
      transition="scale-transition"
      dismissible
    >
      Unauthorized time interval, please try again.
    </v-alert>
    <animation-player
      v-if="!alert"
      :selected-raster-layer="selectedRasterLayer"
      :selected-vector-layer="selectedVectorLayer"
      :service-card-id="serviceCardId"
      :animation-limit-range-array="animationLimitRangeArray"
      :animation-limit-range-array-policy="animationLimitRangeArrayPolicy"
      :animation-step-date-type="animationStepDateType"
      :animation-step="animationStep"
      :min-step="minStep"
      :max-step="maxStep"
      :is-animation="isAnimation"
      :pause-animation="pauseAnimation"
      :overlay="overlay"
      :service-name="serviceName"
      :layer-type="layerType"
      :animation-time-array="animationTimeArray"
      :animation-empty-steps="animationEmptySteps"
      :animation-start-index="animationStartIndex"
      :no-features-found="noFeaturesFound"
      :no-wmts-features-found="noWmtsFeaturesFound"
      @no-raster-features-found="$emit('no-raster-features-found', $event)"
      @no-vector-features-found="$emit('no-vector-features-found', $event)"
    />
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex';
import { BINARY_BASE_URL } from '@/constants.js';
import { getFormattedCurrentDate } from '@/utils.js';

export default {
  components: {
    AnimationPlayer: () => import('@/components/utils/AnimationPlayer')
  },
  props: {
    serviceCardId: {
      type: String,
      default: ''
    },
    animationStepDateType: {
      type: String,
      default: ''
    },
    animationStep: {
      type: String,
      default: ''
    },
    layerType: {
      type: String,
      default: ''
    },
    serviceName: {
      type: String,
      default: ''
    },
    isAnimation: {
      type: Boolean,
      default: false
    },
    pauseAnimation: {
      type: Boolean,
      default: false
    },
    overlay: {
      type: Boolean,
      default: false
    },
    minStep: {
      type: Date,
      default: null
    },
    maxStep: {
      type: Date,
      default: null
    },
    selectedRasterLayer: {
      type: Object,
      default: null
    },
    selectedVectorLayer: {
      type: Object,
      default: null
    },
    animationLimitRangeArray: {
      type: Array,
      default: () => []
    },
    animationLimitRangeArrayPolicy: {
      type: Array,
      default: () => []
    },
    animationTimeArray: {
      type: Array,
      default: () => []
    },
    animationEmptySteps: {
      type: Array,
      default: () => []
    },
    animationStartIndex: {
      type: Number,
      default: 0
    },
    noFeaturesFound: {
      type: Boolean,
      default: false
    },
    noWmtsFeaturesFound: {
      type: Boolean,
      default: false
    },
    tableChartRangeDate: {
      type: String,
      default: ''
    },
    checkPolicyLimits: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      forecastStartTime: getFormattedCurrentDate(this.minStep).split(' ')[1],
      forecastStartDate: getFormattedCurrentDate(this.minStep).split(' ')[0],
      minDate: '',
      maxDate: '',
      forecastRangeMenu: false,
      alert: false,
      isManualDate: false,
      renderAnimation: true
    };
  },
  computed: {
    ...mapState(['map', 'app_user', 'wsServices']),
    dailyData() {
      return this.animationStepDateType === 'dynamic_months' || this.animationStepDateType === 'dynamic_days' || (this.animationStepDateType === 'dynamic_hours' && this.animationStep >= 24);
    },
    dailyVector() {
      return this.dailyData && this.layerType === 'VECTOR';
    }
  },
  watch: {
    $route() {},
    animationTimeArray: {
      async handler(newValue) {
        if (newValue && newValue.length > 0) {
          if (new Date(newValue[0].split('/')[0]) > new Date(this.forecastStartDate + ' ' + this.forecastStartTime)) {
            const [date, hour] = getFormattedCurrentDate(new Date(newValue[0])).split(' ');
            this.forecastStartDate = date;
            this.forecastStartTime = hour;
            this.changeDate();
          }

          this.changeMinMaxDates();

          if (this.isManualDate) {
            this.openSelectedLayer();
            this.isManualDate = false;
          }
        }
      }
    }
  },
  created() {
    if (this.animationTimeArray.length <= 0) {
      const [date, hour] = getFormattedCurrentDate(new Date(this.getFirstStepForecast())).split(' ');
      this.forecastStartDate = date;
      this.forecastStartTime = hour;
    }

    if (!this.overlay) {
      const textfieldDate = this.animationTimeArray[this.animationStartIndex].split('/')[0];
      const [date, hour] = getFormattedCurrentDate(new Date(textfieldDate)).split(' ');

      this.forecastStartDate = date;
      this.forecastStartTime = hour;
    }

    this.changeMinMaxDates();
  },
  methods: {
    ...mapActions(['removeLayer', 'createVectorLayer', 'loadRasterLayer', 'resetAnimationTime', 'removeLayerByServiceID', 'getLayerExtent']),
    changeMinMaxDates() {
      this.minDate = getFormattedCurrentDate(this.minStep);
      this.maxDate = getFormattedCurrentDate(this.dailyVector ? new Date(this.checkPolicyLimits.futureRangeTime - 1) : this.maxStep);
    },
    disableConfirmButton() {
      if (this.map.disableAnimationPlay) {
        return true;
      }

      if (this.dailyData) {
        if (this.layerType === 'RASTER') {
          return !((this.forecastStartDate !== '' && Object.keys(this.selectedRasterLayer).length !== 0));
        } else {
          return !((this.forecastStartDate !== '' && Object.keys(this.selectedVectorLayer).length > 0));
        }
      } else {
        if (this.layerType === 'RASTER') {
          return !((this.forecastStartDate !== '' && this.forecastStartTime !== '' && Object.keys(this.selectedRasterLayer).length !== 0));
        } else {
          return !((this.forecastStartDate !== '' && this.forecastStartTime !== '' && Object.keys(this.selectedVectorLayer).length > 0));
        }
      }
    },
    async changeDate() {
      this.$emit('reset-min-max-step');
      const userTime = new Date(`${this.forecastStartDate} ${this.forecastStartTime}`);
      this.isManualDate = true;

      if (this.layerType === 'VECTOR') {
        await this.removeLayerByServiceID(this.serviceCardId);
      }

      if (this.checkPolicyLimits.pastRangeTime <= userTime.getTime() && this.checkPolicyLimits.futureRangeTime >= userTime.getTime()) {
        this.alert = false;
        this.wsServices.alertPeriod = false;
        await this.$emit('set-animation-steps', [userTime]);
      } else {
        this.alert = true;
        this.wsServices.alertPeriod = true;
      }
    },
    async openSelectedLayer() {
      if (this.layerType === 'RASTER') {
        const extent = await this.getLayerExtent([this.selectedRasterLayer.ws_name, this.selectedRasterLayer.layer_name, this.selectedRasterLayer.layer_type, this.selectedRasterLayer.service_id]);
        const rasterParams = {
          url: `${BINARY_BASE_URL}/fe_ogc/geoserver/wms`,
          params: {
            ENV: '',
            GROUP: this.$route.params.id,
            LAYERS: `${this.selectedRasterLayer.ws_name}:${this.selectedRasterLayer.layer_name}`,
            TIME: this.animationTimeArray[this.animationStartIndex],
            STYLES: this.selectedRasterLayer.layer_style,
            USERNAME: this.app_user.username,
            SERVICE_NAME: this.serviceName,
            FORMAT: 'image/vnd.jpeg-png8'
          },
          TYPE: 'RASTER',
          LAYER_SERVICE_ID: this.serviceCardId,
          UDM: this.selectedRasterLayer.udm,
          UDM_CONVERSIONS: this.selectedRasterLayer.udm_conversions,
          IS_MULTI_VARIABLE: this.selectedRasterLayer.is_multi_variable,
          MULTI_VARIABLE_LAYERS: this.selectedRasterLayer.multi_variable_layers || [],
          MULTI_VARIABLE_LAYER_CONFIGS: this.selectedRasterLayer.multi_variable_layer_configs || [],
          EXTENT: extent
        };

        this.map.rasterMarkerDate = rasterParams.params.TIME;
        await this.loadRasterLayer(rasterParams);
      } else {
        await this.openVectorLayer();
      }

      this.renderAnimation = false;
      this.$nextTick(() => {
        this.renderAnimation = true;
      });
      this.resetAnimationTime();
      clearInterval(this.map.refreshIntervalID[this.serviceCardId]);
      this.$emit('set-refresh-interval');
    },
    async openVectorLayer() {
      await this.removeLayer(`${this.selectedVectorLayer.ws_name}:${this.selectedVectorLayer.layer_name}`);

      this.map.sliderOpacityValue = this.selectedVectorLayer.layer_opacity;

      const extent = await this.getLayerExtent([this.selectedVectorLayer.ws_name, this.selectedVectorLayer.layer_name, this.selectedVectorLayer.layer_type, this.selectedVectorLayer.service_id]);
      const vectorParams = {
        params: {
          GROUP: this.$route.params.id,
          LAYERS: `${this.selectedVectorLayer.ws_name}:${this.selectedVectorLayer.layer_name}`,
          TIME: this.animationTimeArray[this.animationStartIndex],
          STYLES: this.selectedVectorLayer.layer_style,
          USERNAME: this.app_user.username,
          SERVICE_NAME: this.serviceName,
          PARAM_RANGE: this.map.vectorParamRange[this.serviceCardId]
        },
        TYPE: 'VECTOR',
        LAYER_SERVICE_ID: this.serviceCardId,
        UDM: this.selectedVectorLayer.udm,
        UDM_CONVERSIONS: this.selectedVectorLayer.udm_conversions,
        EXTENT: extent,
        AGGREGATION_INTERVAL: this.selectedVectorLayer.aggregation_interval ?? null
      };
      await this.createVectorLayer(vectorParams);
    },
    getFirstStepForecast() {
      const now = new Date();
      const result = new Date(now); // Cloniamo la data per evitare di modificare `now`

      switch (this.animationStepDateType) {
        case 'dynamic_minutes': {
          const minutes = result.getMinutes();
          const extraMinutes = this.animationStep - (minutes % this.animationStep);
          result.setMinutes(minutes + extraMinutes, 0, 0);
          break;
        }

        case 'dynamic_hours': {
          const minutes = result.getMinutes();
          const hours = result.getHours();
          const extraHours = this.animationStep - (hours % this.animationStep);

          if (minutes > 0) {
            result.setHours(hours + extraHours, 0, 0, 0);
          } else {
            const targetHour = hours % this.animationStep === 0 ? hours : hours + extraHours;
            result.setHours(targetHour, 0, 0, 0);
          }

          break;
        }

        case 'dynamic_days': {
          const hours = result.getHours();
          const days = result.getDate();
          const extraDays = this.animationStep - ((days - 1) % this.animationStep);

          if (hours > 0 || result.getMinutes() > 0) {
            result.setDate(days + extraDays);
            result.setHours(0, 0, 0, 0);
          } else {
            const targetDay = (days - 1) % this.animationStep === 0 ? days : days + extraDays;
            result.setDate(targetDay);
            result.setHours(0, 0, 0, 0);
          }

          break;
        }

        case 'dynamic_months': {
          let year = result.getFullYear();
          let month = result.getMonth(); // 0-indexed
          month += this.animationStep;

          if (month > 11) {
            year += 1;
            month = month % 12;
          }

          result.setFullYear(year);
          result.setMonth(month);
          result.setDate(1);
          result.setHours(0, 0, 0, 0);
          break;
        }

        default:
          return null;
      }

      return result.toISOString();
    },
    isLandscape() {
      return /Mobi|Android/i.test(navigator.userAgent) && window.innerHeight < window.innerWidth;
    },
    mobileBreakpoints() {
      return /Mobi|Android/i.test(navigator.userAgent) || this.isLandscape();
    }
  }
};
</script>

<style scoped>
.v-text-field >>> label {
  font-size: 0.8em;
}
.RMprimary-color {
  color: #00A644;
}
</style>
